<template>
  <div class="facilities-list">
    <div class="mt-2 mb-4">
      <v-text-field
        v-if="!previewMode"
        class="shadow"
        v-model="searchInputValue"
        :placeholder="placeholder"
        prepend-inner-icon="icon-search"
        clearable
        outlined
        rounded
        :hide-details="true"
        @input="$emit('on-facility-search', searchInputValue)"
      ></v-text-field>
    </div>
    <Pagination
      :page="page"
      :per-page="perPage"
      :options="pageOptions"
      :total-count="totalCount"
      @change="$emit('handle-change', $event)"
    />
    <v-list two-line ripple v-if="totalCount">
      <v-list-item @click="$emit('toggle-all-facilities')" v-if="!previewMode">
        <template v-slot:default="{ active }">
          <v-list-item-action>
            <v-checkbox :input-value="isAllSelected" />
          </v-list-item-action>

          <v-list-item-content>
            <v-list-item-title>
              {{ $t("message.common.selectAll") }}
              <ItemsCounter
                v-if="selectedCount"
                :count="selectedCount"
                class="float-right"
              />
            </v-list-item-title>
          </v-list-item-content>
        </template>
      </v-list-item>
      <v-list-item
        v-for="item in items"
        :key="item.id"
        @click="!previewMode && $emit('toggle-facilities', item)"
      >
        <template v-slot:default="{ active }">
          <v-list-item-action v-if="member.type === accountTypes.MANAGER">
            <v-checkbox
              :input-value="isSelected(item.id)"
              :error="hasError"
              v-if="!previewMode"
            />
          </v-list-item-action>
          <IsOwnerIndicator
            v-if="item.facilityListings"
            :isOwner="item.facilityListings.isOwner"
            class="mr-4"
          />
          <v-list-item-content>
            <v-list-item-title class="list-element-title">
              <span>{{ item.name }}</span>
              <span
                class="list-element-title__show-more"
                v-if="
                  (!previewMode && isSubtitleQuantity) || (previewMode && group)
                "
                @click.stop="$emit('show-group-details', item.id.toString())"
                >{{ $t("message.common.see") }}</span
              >
            </v-list-item-title>
            <v-list-item-subtitle>
              {{
                isSubtitleQuantity
                  ? getFacilitiesCountLabel(item[itemSubtitleField])
                  : item[itemSubtitleField]
              }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-list-item>
      <ui-alert
        v-if="hasError"
        color="pink"
        icon="mdi-information"
        class="validation-alert"
      >
        {{ $t("message.permissions.atLeastOneFacilityRequired") }}
      </ui-alert>
    </v-list>
    <NoData v-else />
    <Pagination
      :page="page"
      :per-page="perPage"
      :options="pageOptions"
      :total-count="totalCount"
      :show-options="false"
      @change="$emit('handle-change', $event)"
    />
    <SectionLoader :status="listLoading" />
  </div>
</template>

<script>
import Pagination from "@/components/List/Pagination";
import ItemsCounter from "@/components/List/ItemsCounter";
import SectionLoader from "@/components/Loader/SectionLoader";
import NoData from "@/components/ui/NoData";
import IsOwnerIndicator from "@/components/Permissions/IsOwnerIndicator.vue";
import { AccountType } from "@/enums/account-type";
import { FacilityPermissionsEnum } from "@/enums/facility-permissions.enum";
import { pluralCount } from "@/helpers/helpers";

export default {
  name: "FacilitiesListContent",
  components: {
    Pagination,
    ItemsCounter,
    SectionLoader,
    NoData,
    IsOwnerIndicator
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    showError: {
      type: Boolean,
      default: false
    },
    isSubtitleQuantity: {
      type: Boolean,
      default: false
    },
    previewMode: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Function
    },
    member: {
      type: Object,
      required: true
    },
    listLoading: {
      type: Boolean,
      default: false
    },

    totalCount: {
      type: Number,
      required: true
    },
    selectedCount: {
      type: Number
    },
    page: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    pageOptions: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      required: true
    },
    itemSubtitleField: {
      type: String,
      required: true
    },
    isAllSelected: {
      type: Boolean,
      default: false
    },
    permissionType: {
      type: String,
      default: ""
    },
    group: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasError() {
      return this.showError && !this.selectedCount;
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      searchInputValue: "",
      checkbox: false,
      accountTypes: AccountType,
      facilityPermissionsEnum: FacilityPermissionsEnum
    };
  },
  methods: {
    getFacilitiesCountLabel(count) {
      return pluralCount("message.pluralForms.facility", count);
    }
  },
  watch: {
    permissionType: {
      immediate: true,
      handler(current) {
        if (!this.searchInputValue) return;
        this.searchInputValue = "";
        if (current === this.facilityPermissionsEnum.SELECT_GROUPS) {
          this.$emit("on-facility-search", this.searchInputValue);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.facilities-list {
  .list-element-title {
    display: flex;
    justify-content: space-between;
    &__show-more {
      font-weight: bold;
      text-decoration: underline;
    }
  }
  .v-list {
    margin: 0 $spacing * -2;
  }

  .v-list-item {
    padding-right: $spacing * 4;
  }

  .v-list-item__action {
    margin-right: $spacing * 2;

    .value-icon {
      margin-right: $spacing * 1.5;
    }

    .muted {
      opacity: 0.4;
    }
  }

  .v-list-item__title {
    @include font-size(14px);
    line-height: 17px;
    font-weight: 500;
    color: $text;
  }

  .v-list-item__subtitle {
    @include font-size(12px);
    line-height: 15px;
    font-weight: normal;
    color: $text;
  }
}
</style>
